import useIsTouchDevice from "@/hooks/ui-helpers/use-is-touch-device";
import { TitleAndDescription } from "./title-and-description";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { cn } from "@repo/utils/utils";
import { Pencil, X } from "lucide-react";
import {
  ComponentTemplateTypes,
  EntityViewComponentMapping,
  toComponentTemplates,
} from "@/services/entity-view-components.service";
import { renderTemplate } from "@/lib/template-string";
import { useMemo } from "react";
import { CardWithImage } from "./card-with-image";
import { useEntityStore } from "@/stores/entity.store";

export const getComponentFromType = (type: ComponentTemplateTypes) => {
  switch (type) {
    case "title-and-description":
      return TitleAndDescription;
    case "card-with-image":
      return CardWithImage;
    default:
      return null;
  }
};

export type PendingChange = "new" | "edited";

export const pendingChangeStyles = "border-emerald-200 bg-emerald-50";

export const PendingChangeBadge = ({ change }: { change: PendingChange }) => (
  <span
    className="flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-700"
    title="Saved together with this record"
  >
    <span className="size-1.5 rounded-full bg-emerald-500" />
    {change === "new" ? "New" : "Edited"}
  </span>
);

export interface RelationTemplateProps {
  config: {
    [key: string]: string;
  };
  onEdit?: () => void;
  onRemove?: () => void;
  pendingChange?: PendingChange;
}

interface RelationTemplateComponentRendererProps {
  id: string;
  option: { id: string; [fieldName: string]: string } | undefined;
  onOptionClick?: (id: string) => void;
  onDelete?: (id: string) => void;
  isHighlighted?: boolean;
  pendingChange?: PendingChange;
  entityName: string;
  integer_id?: string;
  relationFieldToDisplay?: string;
  mapping?: EntityViewComponentMapping;
}

const getFieldValue = (field: string, value: { [key: string]: unknown } | undefined) => {
  if (!value) return "";
  const paths = field.split(".");

  const fValue = value[paths[0]];
  if (paths.length === 1) {
    return fValue as string;
  }

  // @ts-expect-error because of the generalization and the unknown value
  const dataField = fValue?.data;
  if (!dataField) {
    // For flat data structure, try direct access
    const directValue = value[field];
    if (directValue !== undefined) {
      return directValue as string;
    }
    return "";
  }
  if (Array.isArray(dataField)) {
    if (dataField.length === 0) return "";
    return getFieldValue(paths.slice(1, undefined).join("."), dataField[0]);
  }

  //we got object
  if (typeof dataField === "object") {
    return getFieldValue(paths.slice(1, undefined).join("."), dataField);
  } else {
    console.error("we have got an unknown field value", field, value);
    return "";
  }
};

export const ViewComponentRenderer: React.FC<RelationTemplateComponentRendererProps> = ({
  option,
  isHighlighted,
  pendingChange,
  entityName,
  relationFieldToDisplay,
  mapping,
  onOptionClick,
  id,
  onDelete,
}) => {
  const Component = mapping?.component ? getComponentFromType(mapping?.component) : null;

  const conf = useMemo(() => {
    if (!option) return {};
    return Object.fromEntries(
      Object.entries(toComponentTemplates(mapping?.config)).map(([componentField, template]) => [
        componentField,
        renderTemplate(template, (path) => getFieldValue(path, option)).trim(),
      ]),
    );
  }, [option, mapping]);

  if (Component != null) {
    return (
      <Component
        config={conf}
        onEdit={() => onOptionClick?.(id)}
        onRemove={() => onDelete?.(id)}
        pendingChange={pendingChange}
      />
    );
  }

  if (!option) return null;
  return (
    <FallbackItem
      key={option.id}
      option={option}
      id={option.id}
      onDelete={onDelete}
      onOptionClick={onOptionClick}
      isHighlighted={isHighlighted}
      pendingChange={pendingChange}
      entityName={entityName}
      integer_id={option.integer_id}
      relationFieldToDisplay={relationFieldToDisplay}
    />
  );
};

export const highlightedStyles = " ring-2 ring-secondary ring-inset ";
interface FallbackItemProps {
  id: string;
  option: { id: string; [fieldName: string]: string } | undefined;
  onOptionClick?: (id: string) => void;
  onDelete?: (id: string) => void;
  isHighlighted?: boolean;
  pendingChange?: PendingChange;
  entityName: string;
  integer_id?: string;
  relationFieldToDisplay?: string;
}
const FallbackItem: React.FC<FallbackItemProps> = ({
  option,
  id,
  onDelete,
  isHighlighted,
  pendingChange,
  onOptionClick,
  entityName,
  integer_id,
  relationFieldToDisplay,
}) => {
  const isTouch = useIsTouchDevice();
  const {
    state: { field },
  } = useEntityInstanceLayoutStore(entityName);
  const { getEntityByName } = useEntityStore();

  // Use the custom field if specified, otherwise use the default field
  const displayField = relationFieldToDisplay || field;
  const displayValue = useMemo(() => {
    return option?.[displayField] ?? "";
  }, [option, displayField]);

  return (
    <div
      className={cn(
        "group flex h-10 items-center justify-between rounded-md border border-input py-1 pl-4 pr-3 text-primary",
        { [highlightedStyles]: isHighlighted, [pendingChangeStyles]: pendingChange },
      )}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left text-muted-foreground hover:opacity-80"
        title={`Open ${entityName} to edit`}
        onClick={() => onOptionClick?.(id)}
      >
        {!displayValue && integer_id ? (
          <span className="truncate">{`Item with numeric id: ${integer_id}`}</span>
        ) : (
          <span className="truncate">{displayValue}</span>
        )}
        <Pencil className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" />
      </button>
      {pendingChange && <PendingChangeBadge change={pendingChange} />}
      <button
        type="button"
        title="Disconnect"
        className={cn(
          "ml-2 flex-shrink-0 p-1 text-muted-foreground hover:text-foreground",
          isTouch ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
        onClick={() => onDelete?.(id)}
      >
        <X size={16} />
      </button>
    </div>
  );
};
